import { Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import type { ObjectChecklistCondition, ObjectFilter, SelectOption } from "../../api/types/objectTypes";
import ObjectFilterEditorContent from "./ObjectFilterEditorContent";
import ObjectFilterFieldSelect, { type ObjectFilterFieldGroup } from "./ObjectFilterFieldSelect";
import { findFieldInGroups, getConditionFieldSelectOptions, getDefaultFilterForFieldType } from "./objectFilterHelpers";

interface Props {
  condition: ObjectChecklistCondition;
  availableFields: ObjectFilterFieldGroup[];
  onConditionChange: (condition: ObjectChecklistCondition) => void;
  selectOptions?: SelectOption[];
  startLabel: string;
  error?: string;
}

const ObjectChecklistConditionEditor = ({
  condition,
  availableFields,
  onConditionChange,
  selectOptions,
  startLabel,
  error,
}: Props) => {
  const fieldSelectOptions = useMemo(
    () =>
      selectOptions ??
      getConditionFieldSelectOptions(findFieldInGroups(availableFields, condition.objectType, condition.field.fieldId)),
    [availableFields, condition.objectType, condition.field.fieldId, selectOptions]
  );

  const handleFilterChange = (filter: ObjectFilter) => {
    onConditionChange({ ...condition, field: filter });
  };

  return (
    <Stack spacing={0.5} sx={{ width: "100%" }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Typography color="textSecondary" sx={{ minWidth: "3rem" }}>
          {startLabel}
        </Typography>

        <ObjectFilterFieldSelect
          value={
            condition.objectType && condition.field.fieldId
              ? { groupKey: condition.objectType, fieldId: condition.field.fieldId }
              : undefined
          }
          fieldGroups={availableFields}
          onChange={({ groupKey, field }) =>
            onConditionChange({
              objectType: groupKey,
              field: { fieldId: field.id, ...getDefaultFilterForFieldType(field.type) },
            })
          }
          sx={{ flex: "0 0 12.5rem" }}
        />

        <ObjectFilterEditorContent
          filter={condition.field}
          onFilterChange={handleFilterChange}
          selectOptions={fieldSelectOptions}
        />
      </Stack>
      {error && (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      )}
    </Stack>
  );
};

export default ObjectChecklistConditionEditor;
